// "Put another way" - fun, real-terms spending comparisons.
// Curated list of UK items with approximate average prices. These barely move,
// so refresh by hand once or twice a year rather than pulling live data.
// Each item: e = emoji, n = plural noun, p = price (GBP), yr = subscription
// (rolls months into years when the count gets silly).

export const COMPARISONS = [
  // Cheap and iconic
  { e: '🍫', n: 'Freddos', p: 0.25 },
  { e: '🍫', n: 'king-size Freddos', p: 0.40 },
  { e: '🐟', n: 'tins of tuna', p: 1.00 },
  { e: '🥐', n: 'Greggs sausage rolls', p: 1.30 },
  { e: '🍜', n: 'Pot Noodles', p: 1.20 },
  { e: '🥔', n: 'packets of crisps', p: 1.00 },
  { e: '🧻', n: 'toilet rolls', p: 0.40 },
  { e: '🥛', n: 'pints of milk', p: 0.70 },
  { e: '🍞', n: 'loaves of bread', p: 1.40 },
  { e: '🍫', n: 'Mars bars', p: 0.85 },
  { e: '🥤', n: 'cans of Coke', p: 0.85 },
  { e: '🍫', n: 'Dairy Milk bars', p: 1.40 },
  { e: '📮', n: 'first-class stamps', p: 1.70 },
  { e: '🍬', n: 'bags of Maltesers', p: 1.50 },

  // Out and about
  { e: '🥪', n: 'meal deals', p: 3.40 },
  { e: '🍺', n: 'pints', p: 4.80 },
  { e: '🍺', n: 'London pints', p: 6.50 },
  { e: '☕', n: 'flat whites', p: 3.60 },
  { e: '🍔', n: 'Big Macs', p: 4.49 },
  { e: '🥧', n: 'Greggs steak bakes', p: 1.90 },
  { e: '🍟', n: 'fish and chips', p: 9.50 },
  { e: '🍗', n: "Nando's quarter chickens", p: 6.95 },
  { e: '🍕', n: "Domino's medium pizzas", p: 16.99 },
  { e: '🍦', n: '99 Flakes', p: 2.50 },
  { e: '🥖', n: 'Cornish pasties', p: 3.50 },
  { e: '🥪', n: 'Subway footlongs', p: 6.49 },
  { e: '🍗', n: 'KFC bargain buckets', p: 14.99 },
  { e: '🍺', n: 'pints of Guinness', p: 5.20 },
  { e: '🎟️', n: 'cinema tickets', p: 11.50 },
  { e: '🎰', n: 'lottery tickets', p: 2.00 },

  // Subscriptions (roll into years)
  { e: '📺', n: 'Netflix', p: 12.99, yr: true },
  { e: '🎵', n: 'Spotify', p: 11.99, yr: true },
  { e: '🏰', n: 'Disney+', p: 8.99, yr: true },
  { e: '📦', n: 'Amazon Prime', p: 8.99, yr: true },
  { e: '🎮', n: 'Game Pass', p: 12.99, yr: true },
  { e: '📡', n: 'NOW', p: 9.99, yr: true },

  // Transport and household
  { e: '⛽', n: 'tanks of petrol', p: 75.00 },
  { e: '⛽', n: 'litres of petrol', p: 1.40 },
  { e: '🚌', n: 'bus fares', p: 2.00 },
  { e: '🚇', n: 'Tube journeys', p: 2.80 },
  { e: '🛒', n: 'family food shops', p: 85.00 },
  { e: '📺', n: 'years of TV licence', p: 174.50 },
  { e: '🚕', n: 'Ubers across town', p: 12.00 },
  { e: '💇', n: 'haircuts', p: 18.00 },

  // Bigger-ticket
  { e: '🎮', n: 'PS5 games', p: 59.99 },
  { e: '🎤', n: 'concert tickets', p: 65.00 },
  { e: '✈️', n: 'return flights to Spain', p: 120.00 },
  { e: '🚗', n: 'driving lessons', p: 35.00 },
  { e: '🎪', n: 'festival tickets', p: 250.00 },
  { e: '📱', n: 'iPhones', p: 999.00 },
  { e: '🔧', n: 'MOTs', p: 54.85 },
  { e: '🏋️', n: 'gym day passes', p: 8.00 },
]

function shuffle(arr) {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Returns { hero, items } or null if the spend is too small for any item.
export function pickComparisons(spend, count = 4) {
  if (!spend || spend <= 0) return null
  const pool = shuffle(COMPARISONS.filter(it => spend / it.p >= 1))
  if (pool.length === 0) return null
  return { hero: pool[0], items: pool.slice(1, count + 1) }
}

// Turns an item + spend into a display number and caption.
export function formatComparison(item, spend) {
  const count = Math.round(spend / item.p)
  if (item.yr && count >= 24) {
    return { big: `${Math.round(count / 12).toLocaleString()} yrs`, caption: `of ${item.n}` }
  }
  if (item.yr) {
    return { big: count.toLocaleString(), caption: `months of ${item.n}` }
  }
  return { big: count.toLocaleString(), caption: item.n }
}
